import React, { memo } from 'react';
import { Play, Star, Tv, Check, Plus, X } from 'lucide-react';
import { subtitleFor } from '../lib/catalog';
import {
  POSTER_SIZES, STILL_SIZES, posterSet, posterSrc, stillSet, stillSrc,
} from '../lib/images';

/**
 * L'affiche — brique unique de tout le catalogue.
 *
 * Un seul composant sert la grille, les rails et le Top 10 : c'est ce qui
 * garantit que rien ne dépareille d'un écran à l'autre. Toute la mise en
 * forme vit dans `catalog.css` (classes `.poster*`) ; aucun style inline.
 *
 * @param {object}   title      titre groupé (film ou série)
 * @param {Function} onOpen     ouverture de la fiche
 * @param {Function} [onToggleList] bascule « ma liste »
 * @param {boolean}  [inList]   présent dans « ma liste »
 * @param {boolean}  [watched]  déjà vu → l'affiche se retire visuellement
 * @param {number}   [progress] progression 0-100 → ruban en bas d'affiche
 * @param {number}   [index]    rang dans la liste, pour la cascade d'entrée
 */
function Poster({
  title,
  onOpen,
  onToggleList,
  inList = false,
  watched = false,
  progress = 0,
  index = 0,
  showMeta = true,
  wide = false,
  className = '',
  onDismiss,
  dismissLabel = 'Retirer',
}) {
  if (!title) return null;

  const isSeries = title.isGroupedSeries || title.type === 'serie';
  const rating = Number(title.rating) || 0;
  const source = wide ? title.episodeStill || title.backdrop || title.poster : title.poster;
  const artwork = wide ? stillSrc(source) : posterSrc(source);
  const artworkSet = wide ? stillSet(source) : posterSet(source);
  const artworkSizes = wide ? STILL_SIZES : POSTER_SIZES;

  const open = () => onOpen && onOpen(title);

  return (
    <article
      className={`poster${wide ? ' poster-wide' : ''}${watched ? ' is-seen' : ''} ${className}`}
      style={{ '--i': index }}
    >
      {/* Le bouton porte l'action et l'accessibilité ; le reste est décoratif. */}
      <div className="poster-frame">
        <button type="button" onClick={open} className="poster-hit">
          <span className="sr-only">
            Ouvrir {title.title}
            {isSeries ? ' (série)' : ''}
          </span>
        </button>

        {artwork ? (
          <img
            src={artwork}
            srcSet={artworkSet}
            sizes={artworkSizes}
            alt=""
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-[var(--t-faint)]">
            <Tv className="w-7 h-7" aria-hidden="true" />
          </div>
        )}

        <div className="poster-tl">
          {isSeries && (
            <span className="badge badge-neutral">
              <Tv className="w-[11px] h-[11px]" aria-hidden="true" />
              Série
            </span>
          )}
          {watched && (
            <span className="badge badge-ok">
              <Check className="w-[11px] h-[11px]" aria-hidden="true" />
              Vu
            </span>
          )}
        </div>

        <div className="poster-tr">
          {onDismiss && (
            <button
              type="button"
              className="poster-dismiss"
              title={dismissLabel}
              onClick={(event) => {
                event.stopPropagation();
                onDismiss(title);
              }}
            >
              <X aria-hidden="true" />
              <span className="sr-only">
                {dismissLabel} — {title.title}
              </span>
            </button>
          )}
          {rating > 0 && (
            <span className="score">
              <Star aria-hidden="true" />
              {rating.toFixed(1)}
            </span>
          )}
        </div>

        <div className="poster-hover">
          <span className="poster-play" aria-hidden="true">
            <Play />
          </span>
          {onToggleList && (
            <button
              type="button"
              className="btn btn-sm btn-glass btn-icon relative z-[3]"
              aria-pressed={inList}
              onClick={(event) => {
                event.stopPropagation();
                onToggleList(title);
              }}
            >
              {inList ? <Check aria-hidden="true" /> : <Plus aria-hidden="true" />}
              <span className="sr-only">
                {inList ? 'Retirer de ma liste' : 'Ajouter à ma liste'}
              </span>
            </button>
          )}
        </div>

        {progress > 0 && progress < 98 && (
          <div
            className="poster-progress"
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Progression de ${title.title}`}
          >
            <i style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
        )}
      </div>

      {showMeta && (
        <div className="poster-meta">
          <h3 className="poster-name clamp-2">{title.title}</h3>
          <p className="poster-sub">
            {title.year && <span>{title.year}</span>}
            {title.year && subtitleFor(title) && <span className="dot" aria-hidden="true" />}
            <span>{subtitleFor(title)}</span>
          </p>
        </div>
      )}
    </article>
  );
}

export default memo(Poster);
